import { isPdfUrl } from "@/lib/pdfText";
import {
  columnIndex,
  readWorkbookFromBuffer,
  type Workbook,
  type WorkbookSheet
} from "@/lib/xlsx/readWorkbookFromBuffer";

const MAX_CHARS = 400_000;
const ROWS_PER_CHUNK = 15;

/** Az indexelhető dokumentumfájlok fajtái — a kinyerő ág választásához. */
export type DocumentKind = "pdf" | "xlsx";

export function isExcelUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const path = url.split("?")[0] ?? "";
  return /\.xlsx?$/i.test(path);
}

export function isExcelFile(file: { name: string; type?: string }): boolean {
  if (
    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    file.type === "application/vnd.ms-excel"
  ) {
    return true;
  }
  return /\.xlsx?$/i.test(file.name);
}

export function documentKindFromUrl(url: string | null | undefined): DocumentKind | null {
  if (isPdfUrl(url)) return "pdf";
  if (isExcelUrl(url)) return "xlsx";
  return null;
}

/** Storage URL-ből a fájlnév (kereséshez / chunk fejléc). */
export function filenameFromUrl(url: string | null | undefined): string {
  if (!url) return "";
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    return path.split("/").filter(Boolean).pop() ?? "";
  } catch {
    const path = (url.split("?")[0] ?? "").replace(/\\/g, "/");
    return path.split("/").filter(Boolean).pop() ?? "";
  }
}

function sheetLines(sheet: WorkbookSheet): string[] {
  const rowNums = [...sheet.rows.keys()].sort((a, b) => a - b);
  const lines: string[] = [];
  for (const rowNum of rowNums) {
    const row = sheet.rows.get(rowNum);
    if (!row) continue;
    const cells = [...row.entries()]
      .sort((a, b) => columnIndex(a[0]) - columnIndex(b[0]))
      .map(([, value]) => value.replace(/\s+/g, " ").trim())
      .filter(Boolean);
    if (cells.length > 0) lines.push(cells.join(" | "));
  }
  return lines;
}

/**
 * Munkalapok → sima szöveg a kereséshez és a modellnek.
 * Lapnév fejléc, soronként ` | `-vel elválasztott cellák, oszlopbetű szerint rendezve.
 */
export function sheetsToPlainText(workbook: Workbook, fileName?: string): string {
  const parts: string[] = [];
  const filePrefix = fileName?.trim() ? `Fájl: ${fileName.trim()}\n` : "";
  for (const sheet of workbook.sheets) {
    const lines = sheetLines(sheet);
    if (lines.length === 0) continue;
    parts.push(`${filePrefix}[Munkalap: ${sheet.name}]\n${lines.join("\n")}`);
  }
  return parts.join("\n\n");
}

/**
 * Táblázat-tudatos darabolás: fejléc ismétlődik, ~15 adatsor / chunk.
 * A narratív FTS így nem vágja szét a mennyiség-oszlopot a névtől.
 */
export function chunkSpreadsheetText(workbook: Workbook, fileName?: string): string[] {
  const chunks: string[] = [];
  const fileLine = fileName?.trim() ? `Fájl: ${fileName.trim()}` : "";

  for (const sheet of workbook.sheets) {
    const lines = sheetLines(sheet);
    if (lines.length === 0) continue;
    const header = lines[0] ?? "";
    const dataLines = lines.length > 1 ? lines.slice(1) : [];
    if (dataLines.length === 0) {
      chunks.push([fileLine, `[Munkalap: ${sheet.name}]`, header].filter(Boolean).join("\n"));
      continue;
    }
    for (let i = 0; i < dataLines.length; i += ROWS_PER_CHUNK) {
      const slice = dataLines.slice(i, i + ROWS_PER_CHUNK);
      chunks.push(
        [fileLine, `[Munkalap: ${sheet.name}]`, `Fejléc: ${header}`, ...slice]
          .filter(Boolean)
          .join("\n")
      );
    }
  }
  return chunks;
}

/**
 * Már kinyert plain text újradarabolása (metadata reindex).
 * Felismeri a `[Munkalap: …]` blokkokat.
 */
export function chunkSpreadsheetPlainText(text: string): string[] {
  const normalized = text.replace(/\r\n/g, "\n").trim();
  if (!normalized) return [];
  if (!normalized.includes("[Munkalap:")) {
    return [];
  }

  const chunks: string[] = [];
  const blocks = normalized.split(/\n(?=\[Munkalap:)/);
  let fileLine = "";
  for (const block of blocks) {
    const lines = block.split("\n").map((l) => l.trimEnd()).filter((l) => l.length > 0);
    if (lines.length === 0) continue;
    if (lines[0]?.startsWith("Fájl:")) {
      fileLine = lines[0];
      lines.shift();
    }
    const sheetLine = lines.find((l) => l.startsWith("[Munkalap:")) ?? "[Munkalap: ?]";
    const bodyStart = lines.findIndex((l) => l.startsWith("[Munkalap:"));
    const body = bodyStart >= 0 ? lines.slice(bodyStart + 1) : lines;
    if (body.length === 0) {
      chunks.push([fileLine, sheetLine].filter(Boolean).join("\n"));
      continue;
    }
    const header = body[0] ?? "";
    const data = body.slice(1);
    if (data.length === 0) {
      chunks.push([fileLine, sheetLine, header].filter(Boolean).join("\n"));
      continue;
    }
    for (let i = 0; i < data.length; i += ROWS_PER_CHUNK) {
      const slice = data.slice(i, i + ROWS_PER_CHUNK);
      chunks.push(
        [fileLine, sheetLine, `Fejléc: ${header}`, ...slice].filter(Boolean).join("\n")
      );
    }
  }
  return chunks;
}

export async function fetchWorkbookFromUrl(url: string): Promise<{
  workbook: Workbook;
  fileName: string;
}> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`A táblázat letöltése nem sikerült (${res.status}).`);
  const buf = new Uint8Array(await res.arrayBuffer());
  const workbook = await readWorkbookFromBuffer(buf);
  return { workbook, fileName: filenameFromUrl(url) };
}

/** Nyilvános URL-ről kinyeri a táblázat szöveges tartalmát. */
export async function extractExcelTextFromUrl(url: string): Promise<string> {
  const { workbook, fileName } = await fetchWorkbookFromUrl(url);
  return sheetsToPlainText(workbook, fileName).slice(0, MAX_CHARS);
}
